import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { alpha, Backdrop, Link, Typography, styled, SHADOW_LEVEL1 } from '@ori-ui/mui';
import type { VFC } from 'react';
import { useCallback } from 'react';

import { AboutTileImage } from './AboutTileImage';
import { DESKTOP_BREAKPOINT } from '~/constants';
import { useNavigationContext } from '~/contexts/NavigationContext';

const AboutMenuRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[100]}`,
    boxShadow: SHADOW_LEVEL1,
    display: 'flex',
    left: 0,
    position: 'absolute',
    top: 'var(--top-nav-height)',
    transition: 'top var(--top-nav-height-animation)',
    width: '100%',
  },
}));

const AboutTilesWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignItems: 'center',
    borderBottom: `6px solid ${theme.palette.common.white}`,
    columnGap: 6,
    display: 'grid',
    gridAutoColumns: 'minmax(337px, 452px)',
    gridAutoFlow: 'column',
    gridTemplateRows: 325,
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
  },
}));

const AboutTileLink = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    overflow: 'hidden',
    position: 'relative',

    '&:hover, &:focus, &:active': {
      color: theme.palette.common.white,
    },

    '&:hover::before': {
      opacity: 1,
    },

    '&::before': {
      background: alpha(theme.palette.common.black, 0.3),
      content: '""',
      height: '100%',
      left: 0,
      opacity: 0,
      position: 'absolute',
      top: 0,
      width: '100%',
    },
  },
}));

const AboutTileTitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    left: '50%',
    position: 'absolute',
    textAlign: 'center',
    textShadow: theme.shadows[1],
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
  },
}));

const NUMBER_OF_VISIBLE_TILES = 3;

const AboutMenuContent: VFC = () => {
  const { aboutTiles, toggleMenu } = useNavigationContext();
  const slicedAboutTiles = aboutTiles.slice(0, NUMBER_OF_VISIBLE_TILES);
  const onClickOverlay = useCallback(() => toggleMenu(''), [toggleMenu]);

  return (
    <AboutMenuRoot data-testid="about-menu-root">
      <AboutTilesWrapper>
        {slicedAboutTiles.map((tile) => (
          <AboutTileLink
            key={tile.title}
            href={tile.url}
          >
            {tile.imageUrl ? (
              <AboutTileImage
                image={tile.imageUrl}
                name={tile.title}
              />
            ) : null}
            <AboutTileTitle variant="h3">{tile.title}</AboutTileTitle>
          </AboutTileLink>
        ))}
      </AboutTilesWrapper>
      <Backdrop
        open
        data-testid="about-menu-backdrop"
        sx={{ position: 'absolute', zIndex: '-1', width: '100vw', height: '100vh' }}
        onClick={onClickOverlay}
      />
    </AboutMenuRoot>
  );
};

export const AboutMenu: VFC = () => {
  const { activeMenu } = useNavigationContext();
  const isMenuActive = activeMenu === ToggleMenuFlyout.MenuTypes.AboutMenu;

  // no need to render hidden menu
  return isMenuActive ? <AboutMenuContent /> : null;
};
